import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import {
  connectNotificationSocket,
  disconnectNotificationSocket,
} from '@/services/notificationSocket.service';
import { NOTIFICATIONS_QUERY_PREFIX } from '@/hooks/useUnreadNotificationCount';

function setAppBadge(count: number): void {
  try {
    const nav = navigator as Navigator & {
      setAppBadge?: (n: number) => Promise<void>;
      clearAppBadge?: () => Promise<void>;
    };
    if (count > 0 && typeof nav.setAppBadge === 'function') {
      void nav.setAppBadge(count);
    } else if (typeof nav.clearAppBadge === 'function') {
      void nav.clearAppBadge();
    }
  } catch {
    /* unsupported */
  }
}

function clearOsNotification(tag: string): void {
  if (!('serviceWorker' in navigator)) return;
  void navigator.serviceWorker.ready.then((reg) => {
    reg.active?.postMessage({ type: 'CLEAR_NOTIFICATION', tag });
  });
}

/**
 * Live bell + PWA badge via Socket.IO; falls back to poll when disconnected.
 */
export function useNotificationRealtime(): void {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isAuthenticated) {
      disconnectNotificationSocket();
      setAppBadge(0);
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) return;

    const sock = connectNotificationSocket(token);
    if (!sock) return;

    const onBadge = (payload: { count?: number }) => {
      const count = typeof payload?.count === 'number' ? payload.count : 0;
      queryClient.setQueryData([...NOTIFICATIONS_QUERY_PREFIX, 'unread-count'], count);
      setAppBadge(count);
    };

    const onCreated = () => {
      void queryClient.invalidateQueries({ queryKey: [...NOTIFICATIONS_QUERY_PREFIX] });
    };

    const onResolved = (payload: { approval_instance_id?: string; tag?: string }) => {
      if (payload?.tag) clearOsNotification(payload.tag);
      else if (payload?.approval_instance_id) {
        clearOsNotification(`approval:${payload.approval_instance_id}`);
      }
      void queryClient.invalidateQueries({ queryKey: [...NOTIFICATIONS_QUERY_PREFIX] });
      void queryClient.invalidateQueries({ queryKey: ['approval-inbox'] });
    };

    sock.on('badge:count', onBadge);
    sock.on('notification:created', onCreated);
    sock.on('notification:cleared', onResolved);
    sock.on('approval:resolved', onResolved);

    return () => {
      sock.off('badge:count', onBadge);
      sock.off('notification:created', onCreated);
      sock.off('notification:cleared', onResolved);
      sock.off('approval:resolved', onResolved);
      disconnectNotificationSocket();
    };
  }, [isAuthenticated, queryClient]);
}
